// src/layouts/reports/AssignReport.js
// -----------------------------------------------------------------------------
// Assignation d'un CustomReport à des utilisateurs / équipes.
// - Pyramide : ED → GA → SUP → PD (on assigne toujours le niveau immédiatement inférieur)
// - Onglet "Utilisateurs" : sélection unitaire / Select All
// - Onglet "Team" : appliquer / retirer les membres d'une team qui matchent le niveau cible
// - Sauvegarde via saveReportAssignees(reportId, userIds[])  (le service formate le payload)
// -----------------------------------------------------------------------------
// ⚠️ Hypothèse services (à respecter côté "services/reports.js"):
//   - getReportAssignees(reportId) → [user_id, ...]
//   - saveReportAssignees(reportId, userIdsArray) → 200
//   - listAllUsers() → [{id, username, email, global_role, ...}]
//   - listTeams() → [{id, name}, ...]
//   - getTeamMembers(teamId) → soit
//        [ { id:<membershipId>, user:{ id, username, email, ... }, role_level: "ED|GA|SUP|PD" }, ... ]
//      soit (mode simplifié)
//        [ { id:<userId>, username, email, ... }, ... ]
// -----------------------------------------------------------------------------

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Alert,
  Typography,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";

// Services centralisés (assurent l'appel des bons endpoints backend)
import {
  getMe,
  listCustomReportsAssignable,
  getReportAssignees,
  saveReportAssignees,
  listAllUsers,
  listTeams,
  getTeamMembers,
} from "services/reports";

/* ----------------------------------------------------------------------------
   Helpers
---------------------------------------------------------------------------- */

/** Détermine le niveau hiérarchique d'un user (fallback générique) */
const levelFromUser = (u) => {
  if (!u) return null;
  if (u.is_founder || u.is_superuser) return "ED";

  const gr = u.global_role;
  const rawName =
    (typeof gr === "string" && gr) ||
    (gr && typeof gr === "object" && (gr.name || gr.title || gr.code)) ||
    u.role ||
    u.role_level ||
    "";
  const n = String(rawName || "")
    .trim()
    .toLowerCase();

  if (n === "ed" || n.includes("executive")) return "ED";
  if (n === "ga" || n.includes("grants")) return "GA";
  if (n === "sup" || n.includes("supervis")) return "SUP";
  if (n === "pd" || n.includes("programme") || n.includes("program")) return "PD";
  return null;
};

/** Niveau cible à assigner */
const nextTargetLevel = (myLevel) =>
  myLevel === "ED" ? "GA" : myLevel === "GA" ? "SUP" : myLevel === "SUP" ? "PD" : "PD";

/** Force tableau */
const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

/** Extrait { userId, level } à partir d'un item de /teams/:id/members/ */
//  - Si l'API renvoie des "memberships" : { user, role_level }
//  - Si l'API renvoie des users : { id, ... } (sans role_level)
const normalizeTeamMember = (m) => {
  const hasUserObj = !!m?.user;
  const userId = hasUserObj ? m.user.id : m?.id;
  const level = hasUserObj ? m.role_level || levelFromUser(m.user) : levelFromUser(m);
  return { userId, level };
};

/* ----------------------------------------------------------------------------
   Page
---------------------------------------------------------------------------- */

const AssignReport = () => {
  const { reportId: reportIdParam } = useParams(); // si route : /reports/assign/:reportId
  const navigate = useNavigate();

  // Contexte utilisateur
  const [me, setMe] = useState(null);
  const [myLevel, setMyLevel] = useState(null);

  // Rapports & sélection
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(reportIdParam || "");

  // Données d’assignation (par user ID)
  const [users, setUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]); // [userId]
  const [originalAssigned, setOriginalAssigned] = useState([]); // snapshot

  // Teams (application groupée)
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [teamMembers, setTeamMembers] = useState([]); // brut renvoyé par service

  // UI
  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0); // 0 = Utilisateurs, 1 = Team
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  // Calcul du niveau cible
  const targetLevel = useMemo(() => nextTargetLevel(myLevel || "PD"), [myLevel]);

  /* ------------------------------------------
     Chargement initial : /users/me, reports, teams
  ------------------------------------------ */
  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoadingInit(true);
      try {
        // 1) User courant
        const meData = await getMe().catch(() => null);
        if (!isMounted) return;
        setMe(meData);
        setMyLevel(levelFromUser(meData));

        // 2) Rapports assignables (ou fallback)
        const reportsList = toArray(await listCustomReportsAssignable());
        if (!isMounted) return;
        setReports(reportsList);

        // 3) Pré-sélection : URL > premier rapport
        const initialId = reportIdParam || (reportsList.length ? String(reportsList[0].id) : "");
        setSelectedReportId(initialId);

        // 4) Teams
        const myTeams = toArray(await listTeams().catch(() => []));
        if (!isMounted) return;
        setTeams(myTeams);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        if (isMounted) setError("Impossible de charger les données initiales.");
      } finally {
        if (isMounted) setLoadingInit(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [reportIdParam]);

  /* ------------------------------------------
     Chargement assignations du report sélectionné
  ------------------------------------------ */
  const loadAssignment = useCallback(async (rid) => {
    if (!rid) {
      setUsers([]);
      setAssignedUsers([]);
      setOriginalAssigned([]);
      return;
    }
    setLoadingAssign(true);
    try {
      // 1) Tous les utilisateurs du tenant
      const userList = toArray(await listAllUsers());
      setUsers(userList);

      // 2) Assignations actuelles (liste d'IDs)
      const current = toArray(await getReportAssignees(rid));
      setAssignedUsers(current);
      setOriginalAssigned(current);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError("Erreur lors du chargement des assignations.");
    } finally {
      setLoadingAssign(false);
    }
  }, []);

  // Au changement de rapport, recharge les assignations
  useEffect(() => {
    if (selectedReportId) loadAssignment(selectedReportId);
  }, [selectedReportId, loadAssignment]);

  /* ------------------------------------------
     Sélections / Filtres côté "Utilisateurs"
  ------------------------------------------ */
  const handleToggle = (userId) => {
    setAssignedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  // On ne montre que les users du niveau cible
  const filteredUsers = useMemo(() => {
    if (!users?.length) return [];
    if (myLevel === "PD") return []; // PD ne peut pas assigner
    return users.filter((u) => levelFromUser(u) === targetLevel);
  }, [users, myLevel, targetLevel]);

  // Select/Deselect All dans le scope
  const handleSelectAll = () => {
    if (!filteredUsers.length) return;
    const idsInScope = filteredUsers.map((u) => u.id);
    const selectedInScope = assignedUsers.filter((id) => idsInScope.includes(id));
    const allSelected = selectedInScope.length === idsInScope.length;

    if (allSelected) {
      // Uncheck uniquement ceux du scope
      setAssignedUsers((prev) => prev.filter((id) => !idsInScope.includes(id)));
    } else {
      // Check tous ceux du scope (et préserver les autres)
      const next = new Set([...assignedUsers, ...idsInScope]);
      setAssignedUsers([...next]);
    }
  };

  /* ------------------------------------------
     Onglet Team
  ------------------------------------------ */

  // Chargement des membres de la team sélectionnée
  const loadTeamMembers = async (tid) => {
    try {
      setSelectedTeamId(tid);
      if (!tid) {
        setTeamMembers([]);
        return;
      }
      const members = toArray(await getTeamMembers(tid)); // voir normalizeTeamMember()
      setTeamMembers(members);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError("Impossible de charger les membres de la team.");
    }
  };

  // Utilitaire : récupère les IDs de la team qui matchent le niveau cible
  const teamIdsAtTargetLevel = useMemo(() => {
    if (!teamMembers?.length) return [];
    const ids = [];
    for (const m of teamMembers) {
      const { userId, level } = normalizeTeamMember(m);
      if (userId && level === targetLevel) ids.push(userId);
    }
    return ids;
  }, [teamMembers, targetLevel]);

  // Ajoute ces IDs aux assignations (sans doublons)
  const applyTeamMembers = () => {
    if (!teamIdsAtTargetLevel.length) return;
    const next = new Set([...assignedUsers, ...teamIdsAtTargetLevel]);
    setAssignedUsers([...next]);
  };

  // Retire ces IDs des assignations
  const removeTeamMembers = () => {
    if (!teamIdsAtTargetLevel.length) return;
    const toRemove = new Set(teamIdsAtTargetLevel);
    setAssignedUsers((prev) => prev.filter((id) => !toRemove.has(id)));
  };

  /* ------------------------------------------
     Sauvegarde
  ------------------------------------------ */
  const hasChanges =
    JSON.stringify([...assignedUsers].sort()) !== JSON.stringify([...originalAssigned].sort());

  const handleSave = async () => {
    if (!selectedReportId) return;
    try {
      setSaving(true);

      // IMPORTANT : on envoie un tableau d'IDs TenantUser (le service se charge du body attendu)
      await saveReportAssignees(selectedReportId, assignedUsers);

      setOkMsg("Assignations sauvegardées.");

      if (reportIdParam) {
        navigate(`/reports/view/${selectedReportId}`);
      } else {
        setOriginalAssigned(assignedUsers);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Erreur lors de la sauvegarde des assignations :", err);
      setError("Erreur lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------
     Rendu
  ------------------------------------------ */
  if (loadingInit) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!selectedReportId) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Typography variant="h6" sx={{ px: 3, mb: 2 }}>
            Aucun rapport disponible à l’assignation.
          </Typography>
          <Box sx={{ px: 3 }}>
            <Button variant="outlined" onClick={() => navigate("/reports")}>
              Retour aux rapports
            </Button>
          </Box>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const allInScopeSelected =
    filteredUsers.length > 0 &&
    assignedUsers.filter((id) => filteredUsers.some((u) => u.id === id)).length ===
      filteredUsers.length;

  const canApplyTeam = teamIdsAtTargetLevel.length > 0;
  const canRemoveTeam =
    teamIdsAtTargetLevel.length > 0 &&
    assignedUsers.some((id) => teamIdsAtTargetLevel.includes(id));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* Entête */}
        <Grid container justifyContent="space-between" alignItems="center" mb={2} px={3}>
          <Typography variant="h5" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Assign
            <Chip label={`Your level: ${myLevel || "-"}`} />
            <Chip label={`Target: ${targetLevel}`} color="info" variant="outlined" />
          </Typography>

          {!reportIdParam && (
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <Select
                value={selectedReportId}
                onChange={(e) => setSelectedReportId(e.target.value)}
                displayEmpty
              >
                {reports.map((r) => (
                  <MenuItem key={r.id} value={String(r.id)}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Grid>

        {/* Corps */}
        {loadingAssign ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, mb: 2 }}>
              <Tab label="Utilisateurs" />
              <Tab label="Team" />
            </Tabs>

            {/* Onglet Utilisateurs */}
            {tab === 0 && (
              <Grid container spacing={2} px={3}>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={handleSelectAll}
                    disabled={!filteredUsers.length}
                  >
                    {allInScopeSelected ? "Deselect All" : "Select All"}
                  </Button>
                </Grid>

                {filteredUsers.length === 0 && (
                  <Grid item xs={12}>
                    <Typography color="text.secondary">
                      Aucun utilisateur disponible pour le niveau cible ({targetLevel}).
                    </Typography>
                  </Grid>
                )}

                {filteredUsers.map((user) => (
                  <Grid item xs={12} sm={6} md={4} key={user.id}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        bgcolor: assignedUsers.includes(user.id) ? "#e3f2fd" : "#fff",
                        border: assignedUsers.includes(user.id)
                          ? "1px solid #2196f3"
                          : "1px solid #ccc",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={assignedUsers.includes(user.id)}
                            onChange={() => handleToggle(user.id)}
                          />
                        }
                        label={user.username || user.email || `#${user.id}`}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Onglet Team */}
            {tab === 1 && (
              <Grid container spacing={2} px={3}>
                <Grid item xs={12} md={6} lg={4}>
                  <FormControl fullWidth size="small">
                    <Select
                      displayEmpty
                      value={selectedTeamId}
                      onChange={(e) => loadTeamMembers(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Choisir une team…</em>
                      </MenuItem>
                      {teams.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {selectedTeamId && (
                  <>
                    <Grid item xs={12}>
                      <Typography sx={{ mb: 1 }}>
                        Membres ({teamMembers.length}) — seuls les <b>{targetLevel}</b> seront pris
                        en compte pour l’assignation.
                      </Typography>

                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {teamMembers.map((m) => {
                          const hasUserObj = !!m?.user;
                          const u = hasUserObj ? m.user : m; // support des deux formats
                          const lvl = hasUserObj
                            ? m.role_level || levelFromUser(u)
                            : levelFromUser(u);
                          return (
                            <Chip
                              key={(m.id ?? u.id) + "-" + (lvl || "-")}
                              label={`${u?.username || u?.email || "user"} · ${lvl || "-"}`}
                              variant={lvl === targetLevel ? "filled" : "outlined"}
                              color={lvl === targetLevel ? "info" : "default"}
                            />
                          );
                        })}
                      </Box>
                    </Grid>

                    <Grid item xs={12} display="flex" gap={1}>
                      <Button
                        variant="contained"
                        onClick={applyTeamMembers}
                        disabled={!canApplyTeam}
                      >
                        Ajouter les membres de la team
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={removeTeamMembers}
                        disabled={!canRemoveTeam}
                      >
                        Retirer les membres de la team
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            )}

            {/* Actions globales */}
            <Grid container justifyContent="flex-end" mt={4} px={3} spacing={2}>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() =>
                    reportIdParam
                      ? navigate(`/reports/view/${selectedReportId}`)
                      : navigate("/reports")
                  }
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={saving || !selectedReportId || !hasChanges}
                >
                  {saving ? "Saving..." : "Save Assignments"}
                </Button>
              </Grid>
            </Grid>
          </>
        )}

        {/* Snackbars */}
        <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar open={!!okMsg} autoHideDuration={3000} onClose={() => setOkMsg(null)}>
          <Alert severity="success" onClose={() => setOkMsg(null)}>
            {okMsg}
          </Alert>
        </Snackbar>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default AssignReport;
